<template>
  <Teleport to="body">
    <cl-modal
      :close-modal-text="$t('close')"
      :visible="visible"
      :size="'md'"
      test-id="add-msp-modal"
      @on-close="onClose"
    >
      <template #[`header.title`]>
        <cl-heading :size="'h4'">{{ t("add-msp") }}</cl-heading>
      </template>
      <div>
        <section class="tw-mt-6 tw-mb-10">
          <cl-stepper :steps="steps" />
        </section>
        <!-- add msp -->
        <msp-step-index
          v-if="mspStep.current"
          v-model:form="mspForm"
          @form-validation="isMspFormValid = $event"
        />
      </div>
      <!-- add addmin step 2-->
      <admin-step-index
        v-if="adminStep.current"
        v-model:form="adminForm"
        @form-validation="isAdminFormValid = $event"
        v-model:isEmailTaken="isEmailTaken"
        v-model:isEmailRecordWrong="isEmailRecordWrong"
        :trigger-email-validation="triggerEmailValidation"
      />
      <!-- Add Solutions step 3-->
      <solutions-step-index
        v-if="solutionsStep.current"
        v-model:form="solutionsForm"
        @form-validation="isSolutionsFormValid = $event"
        :customer-name="mspForm.name"
        v-model:isCreateMspButtonDisabled="isCreateMspButtonDisabled"
      />
      <!-- footer -->
      <template #[`footer.decline`]>
        <cl-button
          data-test-id="add-msp-btn-cancel"
          :variant="'secondary-ghost'"
          @on-click="onClose"
        >
          <span>{{ t("cancel") }}</span>
        </cl-button>
        <template v-if="adminStep.current">
          <cl-button
            :variant="'secondary-outline'"
            data-test-id="add-msp-input-btn-go-back-to-admin-step"
            @on-click="goToStep(1)"
          >
            <span>{{ t("back") }}</span>
          </cl-button>
        </template>
        <template v-if="solutionsStep.current">
          <cl-button
            data-test-id="add-msp-input-btn-go-back-to-admin-step"
            :variant="'secondary-outline'"
            @on-click="goToStep(2)"
          >
            <span>{{ t("back") }}</span>
          </cl-button>
        </template>
      </template>
      <template #[`footer.accept`]>
        <template v-if="mspStep.current">
          <cl-button
            variant="secondary"
            data-test-id="add-msp-btn-go-to-customers-step"
            :disabled="isMspFormValid"
            @on-click="
              () => {
                mspStep.completed = true;
                goToStep(2);
              }
            "
          >
            <span>{{ t("next") }}</span>
          </cl-button>
        </template>
        <template v-if="adminStep.current">
          <cl-button
            variant="secondary"
            data-test-id="add-msp-btn-go-to-preview-step-btn"
            :disabled="isAdminFormValid"
            @on-click="
              () => {
                adminStep.completed = true;
                goToStep(3);
              }
            "
          >
            <span>{{ t("next") }}</span>
          </cl-button>
        </template>
        <template v-if="solutionsStep.current">
          <cl-button v-if="creatingMsp" :variant="'secondary'" disabled>
            <cl-spinner
              :loading-text="$t('loading')"
              :size="'small'"
              class="mr-2"
            />
            <span>Saving...</span>
          </cl-button>
          <cl-button
            v-else
            variant="secondary"
            data-test-id="save-msp-btn-save-save"
            :disabled="isCreateMspButtonDisabled"
            @on-click="saveMsp"
          >
            <span>{{ t("save-msp") }}</span>
          </cl-button>
        </template>
      </template>
    </cl-modal>
  </Teleport>
</template>
<script setup lang="ts">
import { ref, computed, toRefs } from "vue";
import type { Step } from "@cybersentriq/frontend-component-library/dist/types/src/components/stepper/types";
import { useLicenceStore } from "@/modules/Usage/store/licence.store";
import { useUsersStore } from "@/store/users.store";
import { useArctitanStore } from "@/store/arctitan.store";
import { useOrganisationsStore } from "@/store/organisations.store";
import { isAxiosError } from "axios";
import { DATA_TABLE_COOKIE } from "@/constants";
import { useCookiesDataTable } from "@/composables";

import { TIER } from "@/constants";
import {
  ARC_SERVICE,
  PHISH_SERVICE,
  PHISHING_SIMULATION,
  SAT_SERVICE,
} from "@/utils/services";

import type { Ref } from "vue";

import { useI18n } from "vue-i18n";
import { Organisation } from "@/types/organisatio";
import { Admin } from "@/types/admin";
import { useToastStore } from "@/components/TToasts/toast.store.ts";
import mspStepIndex from "./msp-step/msp-step-index.vue";
import adminStepIndex from "./admin-step/admin-step-index.vue";
import solutionsStepIndex from "./solutions-step/solutions-step-index.vue";

const toastStore = useToastStore();

interface TAddMspsProps {
  visible: boolean;
  parentUuid?: string | null;
}
const cookieKey = DATA_TABLE_COOKIE.SYSTEM_MSPS_LIST;
const { reqParams } = useCookiesDataTable(
  {
    sortByKey: `${cookieKey}-sort-by`,
    sortOrdKey: `${cookieKey}-sort-order`,
    rowsPerPageKey: `${cookieKey}-rows-per-page`,
    selectedColumnsKey: `${cookieKey}-selected-columns`,
  },
  {
    sortBy: "name",
    sortOrd: "asc",
  },
);

const props = defineProps<TAddMspsProps>();
const { visible, parentUuid } = toRefs(props);

const { t } = useI18n();
const organisationsStore = useOrganisationsStore();
const licenceStore = useLicenceStore();
const arctitanStore = useArctitanStore();
const usersStore = useUsersStore();

const isEmailTaken = ref<boolean>(false);
const isEmailRecordWrong = ref<boolean>(false);
const isMspFormValid = ref<boolean>(true);
const isAdminFormValid = ref<boolean>(true);
const isSolutionsFormValid = ref<boolean>(true);
const isCreateMspButtonDisabled = ref<boolean>(false);

const triggerEmailValidation = ref<number>(0);

const mspForm = ref({
  name: "",
  description: "",
  country: "",
  customerID: "",
});

const adminForm = ref({
  name: "",
  email: "",
  password: "",
  confirm_password: "",
  roles: ["super-admin"],
});

const solutionsForm = ref({
  emailSecurity: {
    seats_purchased: "",
    maxioSubID: "",
    switcher: false,
  },
  emailArchiving: {
    seats_purchased: "",
    maxioSubID: "",
    switcher: false,
  },
  phishingSimulation: {
    seats_purchased: "",
    maxioSubID: "",
    switcher: false,
  },
  sat: {
    seats_purchased: "",
    maxioSubID: "",
    switcher: false,
  },
});

const mspStep = ref<Step>({
  index: 1,
  label: t("add-msp"),
  current: true,
  completed: false,
  actionable: false,
});

const adminStep = ref<Step>({
  index: 2,
  label: t("step-add-admin"),
  current: false,
  completed: false,
  actionable: false,
});

const solutionsStep = ref<Step>({
  index: 3,
  label: t("step-add-solutions"),
  current: false,
  completed: false,
  actionable: false,
});

const steps = computed<Step[]>(() => [
  mspStep.value,
  adminStep.value,
  solutionsStep.value,
]);
const stepsRefs = [mspStep, adminStep, solutionsStep];

const goToStep = (index: number | string) => {
  for (const step of stepsRefs) {
    if (index === step.value.index) {
      step.value.current = true;
      step.value.completed = false;

      continue;
    }

    step.value.current = false;
  }
};

const creatingMsp = ref(false);
const creatingMspResponse: Ref<Organisation> = ref({
  uuid: "",
  name: "",
  created_at: "",
  country: "",
  organisation_type: "",
  services: [],
});
const creatingAdminResponse: Ref<Admin> = ref({
  uuid: "",
  name: "",
  email: "",
  organisation_uuid: "",
});

const saveMsp = async () => {
  const isValid = !(
    isMspFormValid.value &&
    isAdminFormValid.value &&
    isSolutionsFormValid.value
  );
  if (!isValid) {
    goToStep(1);
    return;
  }
  try {
    creatingMsp.value = true;
    const orgs = await organisationsStore.createOrginisation({
      formData: mspForm.value,
      parent_uuid: parentUuid?.value ?? null,
      organisation_type: TIER.msps,
    });
    creatingMspResponse.value = orgs.data;
  } catch (e) {
    creatingMsp.value = false;

    /* eslint-disable no-console */
    console.log(e);
  } finally {
    await adminCreate();
  }
};

const enableEmailArchiving = async () => {
  //the user has clicker the slider
  try {
    await Promise.all([
      arctitanStore.createMspArctian({
        formData: {
          name: creatingMspResponse.value.name,
          email: creatingAdminResponse.value.email,
          country: mspForm.value.country,
        },
        uuid: creatingMspResponse.value.uuid,
      }),
      licenceStore.createLicence({
        solution_uuid: ARC_SERVICE,
        uuid: creatingMspResponse.value.uuid,
        formData: solutionsForm.value.emailArchiving,
        organisation_type: TIER.msps,
      }),
    ]);
  } catch (e) {
    /* eslint-disable no-console */
    console.error(e);
  }
};
const finished = async () => {
  creatingMsp.value = false;
  emit("on-create");

  toastStore.displayToast({
    title: t("success"),
    message: t("msp-created"),
    duration: 5000,
    variant: "success",
    testId: "add-msp-toast-success",
  });

  try {
    await organisationsStore.createPhishTitanOrg(
      creatingMspResponse.value.uuid,
    );
    await organisationsStore.getOrginisationsList({
      parent_uuid: parentUuid?.value ?? null,
      params: reqParams,
    });
  } catch (e) {
    /* eslint-disable no-console */
    console.log(e);
  }
};
const hasEmailArchivingTag = async () => {
  try {
    await arctitanStore.hasTagMsp({
      formData: {
        name: creatingMspResponse.value.name,
        country: mspForm.value.country,
      },
      uuid: creatingMspResponse.value.uuid,
    });
  } catch (e) {
    /* eslint-disable no-console */
    console.log(e);
  } finally {
    if (solutionsForm.value.emailSecurity.switcher) {
      await createEmailSecurityLicenses();
    }
    if (solutionsForm.value.phishingSimulation.switcher) {
      await createPhishingSimLicenses();
    }
    if (solutionsForm.value.sat.switcher) {
      await createSATLicenses();
    }
    if (solutionsForm.value.emailArchiving.switcher) {
      await enableEmailArchiving();
    }
    await finished();
  }
};
const createEmailSecurityLicenses = async () => {
  try {
    await licenceStore.createLicence({
      solution_uuid: PHISH_SERVICE,
      formData: solutionsForm.value.emailSecurity,
      uuid: creatingMspResponse.value.uuid,
      organisation_type: TIER.msps,
    });
  } catch (e) {
    /* eslint-disable no-console */
    console.error(e);
  }
};
const createPhishingSimLicenses = async () => {
  try {
    await licenceStore.createLicence({
      solution_uuid: PHISHING_SIMULATION,
      formData: solutionsForm.value.phishingSimulation,
      uuid: creatingMspResponse.value.uuid,
      organisation_type: TIER.msps,
    });
  } catch (e) {
    /* eslint-disable no-console */
    console.error(e);
  }
};
const createSATLicenses = async () => {
  try {
    await licenceStore.createLicence({
      solution_uuid: SAT_SERVICE,
      formData: solutionsForm.value.sat,
      uuid: creatingMspResponse.value.uuid,
      organisation_type: TIER.msps,
    });
  } catch (e) {
    /* eslint-disable no-console */
    console.error(e);
  }
};
const adminCreate = async () => {
  try {
    if (creatingMspResponse.value) {
      const admin = await usersStore.createUser({
        formData: adminForm.value,
        uuid: creatingMspResponse.value.uuid,
      });
      creatingAdminResponse.value = admin.data;
      if (admin.status >= 200 && admin.status < 300) {
        await hasEmailArchivingTag();
      }
    }
  } catch (error: unknown) {
    if (isAxiosError(error) && error.response?.status === 422) {
      const errorMessage = error.response.data.fields[0].errors[0];
      if (errorMessage === "The email has already been taken.") {
        isEmailTaken.value = true;
        adminForm.value.email = "";
        triggerEmailValidation.value++;
      } else if (errorMessage === "The email must be a valid email address.") {
        isEmailRecordWrong.value = true;
        adminForm.value.email = "";
        triggerEmailValidation.value++;
      }
      goToStep(2);
    }
  } finally {
    // Ensure the modal never stays stuck in the loading state, even if
    // admin creation fails or the success branch is skipped.
    creatingMsp.value = false;
  }
};
const emit = defineEmits<{
  (event: "on-close"): void;
  (event: "on-create"): void;
}>();
const onClose = () => {
  emit("on-close");
};
</script>
